/**
 * Composer — campo de entrada para conversas de infra.
 *
 * Comportamento por aba (activeTab):
 *   'conversas'  → postMessage com channel:'conversas'; placeholder "Mensagem para o time…"
 *   'alertas'    → postMessage com channel:'alertas'; placeholder "Comentar este ambiente…"
 *   'atendente'  → fluxo /ask (bot privado); placeholder "Pergunte ao Atendente…"
 *
 * O token @Atendente foi removido — a aba decide o destino.
 * Suporta autocomplete de @menção: dropdown com @todos (sintético, id -2)
 * + usuários via GET /conversations/{cid}/mentionable.
 *
 * SP5: suporte a @todos (mention_all) e reply/citação.
 * SP6: suporte a anexos (upload, paste, chips) — controlado por attachmentsSupported.
 *
 * Props:
 *   cid                  — id da conversa
 *   activeTab            — aba ativa (controla roteamento e placeholder)
 *   onAsk                — callback para enviar ao bot (texto, alertMessageId?)
 *   sendingAsk           — true quando a requisição /ask está em andamento
 *   initialText          — texto pré-preenchido (ex: "Explique este alerta")
 *   alertMessageId       — injetado pelo botão "Explicar"
 *   replyingTo           — mensagem sendo citada (null = sem citação ativa)
 *   onCancelReply        — callback para cancelar citação
 *   attachmentsSupported — habilita botão de clipe + paste de arquivos (padrão: false)
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Popover,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconMoodSmile, IconPaperclip, IconSend, IconX } from '@tabler/icons-react'
import { chatApi } from '@/api/chat'
import type { AskAttachmentRef, ChatMessage, ChatTab, MentionableUser } from '@/api/chat'
import { usePostMessage } from '@/hooks/useChat'
import { EmojiPicker } from './EmojiPicker'
import { convertEmoticonAtCursor, convertEmoticonsAll } from '../emoticons'

// Entrada sintética — @todos
const TODOS_ENTRY: MentionableUser = { id: -2, name: 'todos', email: '' }

const PLACEHOLDERS: Record<ChatTab, string> = {
  conversas: 'Mensagem para o time…',
  alertas: 'Comentar este ambiente…',
  atendente: 'Quer conversar sobre isso?',
}

// ---------------------------------------------------------------------------
// Tipos de upload pendente
// ---------------------------------------------------------------------------

type PendingUpload = {
  localId: string
  file: File
  status: 'uploading' | 'done' | 'error'
  s3_key?: string
}

interface ComposerProps {
  cid: number
  activeTab: ChatTab
  onAsk: (
    text: string,
    alertMessageId?: number,
    shared?: boolean,
    attachments?: AskAttachmentRef[],
  ) => void
  sendingAsk: boolean
  initialText?: string
  /** alertMessageId injetado pelo botão "Explicar" */
  alertMessageId?: number
  /** Mensagem sendo citada (null = sem citação ativa) */
  replyingTo?: ChatMessage | null
  /** Cancela a citação em andamento */
  onCancelReply?: () => void
  /** Habilita botão de clipe + upload de arquivos via paste (padrão: false) */
  attachmentsSupported?: boolean
  /**
   * Quando true (DM 1:1 com o @Atendente), envia SEMPRE via onAsk (/ask),
   * sem exigir seleção manual de @Atendente. Padrão: false.
   */
  forceAtendente?: boolean
  /** Envio ao robô de alguém mencionado no grupo (rota própria, só leitura). */
  onAskRobo?: (botUserId: number, text: string) => void
  /** Nome de quem recebe na DM com `forceAtendente` (o robô do dono). Só muda o placeholder. */
  destino?: string
  /** Armado por "Responder a {robô}" (consoleai#24585): a próxima mensagem vai a
   *  esse robô por `onAskRobo`, como se ele tivesse sido mencionado. */
  responderAoRobo?: { botUserId: number; nome: string } | null
}

export function Composer({
  cid,
  activeTab,
  onAsk,
  sendingAsk,
  initialText,
  alertMessageId,
  replyingTo,
  onCancelReply,
  attachmentsSupported = false,
  forceAtendente = false,
  onAskRobo,
  destino,
  responderAoRobo,
}: ComposerProps) {
  const [value, setValue] = useState(initialText ?? '')
  // Ids de usuários humanos mencionados (id > 0)
  const [mentionUserIds, setMentionUserIds] = useState<number[]>([])
  // true quando @todos foi selecionado
  const [mentionAll, setMentionAll] = useState(false)
  // true quando @Atendente foi selecionado (envia via /ask com shared:true)
  const [askAtendenteShared, setAskAtendenteShared] = useState(false)
  // bot_user_id do robô mencionado (envia à rota do robô, não ao @Atendente)
  const [alvoRobo, setAlvoRobo] = useState<number | null>(null)
  // Controle do dropdown de @menção
  const [popoverOpen, setPopoverOpen] = useState(false)
  // Seletor de emojis (aberto/fechado)
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [mentionPrefix, setMentionPrefix] = useState('')
  const [mentionResults, setMentionResults] = useState<MentionableUser[]>([])
  const [mentionLoading, setMentionLoading] = useState(false)
  // Item destacado no dropdown de menção (navegação por teclado)
  const [highlightIndex, setHighlightIndex] = useState(0)

  // Upload de anexos
  const [uploads, setUploads] = useState<PendingUpload[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const postMessage = usePostMessage(cid)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const lastTypingRef = useRef(0)

  // Limpa o campo ao trocar de aba (evita cruzar mensagem entre abas).
  // IMPORTANTE: este efeito vem ANTES do de prefill abaixo. O botão "Explicar"
  // troca de aba E injeta o prefill no MESMO render → os dois efeitos disparam;
  // como o React roda efeitos na ordem de declaração, limpar PRIMEIRO e aplicar
  // o prefill DEPOIS faz o prefill sobreviver (antes, a limpeza o zerava → o
  // Composer abria vazio ao "Explicar" um alerta).
  useEffect(() => {
    setValue('')
    setMentionUserIds([])
    setMentionAll(false)
    setAskAtendenteShared(false)
    setAlvoRobo(null)
    setPopoverOpen(false)
    setUploads([])
    // Mantém o campo pronto p/ digitar ao trocar de aba (sem precisar clicar).
    setTimeout(() => textareaRef.current?.focus(), 0)
  }, [activeTab])

  // Aplica o texto pré-preenchido quando muda (ex: usuário clicou "Explicar").
  useEffect(() => {
    if (initialText !== undefined) {
      setValue(initialText)
    }
  }, [initialText])

  // ---------------------------------------------------------------------------
  // Upload de arquivos
  // ---------------------------------------------------------------------------

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        const localId = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`
        setUploads((u) => [...u, { localId, file, status: 'uploading' }])
        try {
          const pre = await chatApi.presignAttachment(cid, {
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
          })
          if (!pre.supported || !pre.presigned_put_url || !pre.s3_key) {
            throw new Error('unsupported')
          }
          await chatApi.uploadToS3(pre.presigned_put_url, file)
          setUploads((u) =>
            u.map((x) =>
              x.localId === localId ? { ...x, status: 'done', s3_key: pre.s3_key } : x,
            ),
          )
        } catch {
          setUploads((u) =>
            u.map((x) => (x.localId === localId ? { ...x, status: 'error' } : x)),
          )
        }
      }
    },
    [cid],
  )

  // ---------------------------------------------------------------------------
  // Lógica de @menção
  // ---------------------------------------------------------------------------

  function detectMentionPrefix(text: string, cursorPos: number): string | null {
    const before = text.slice(0, cursorPos)
    const match = before.match(/@([\w.]*)$/)
    return match ? match[1] : null
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    let newValue = e.currentTarget.value
    // Emoticon → emoji ao vivo: quando o usuário digita um separador logo após
    // um emoticon completo (ex.: ";-) " vira "😉 "), converte só aquele token.
    const rawCursor = e.currentTarget.selectionStart ?? newValue.length
    const conv = convertEmoticonAtCursor(newValue, rawCursor)
    if (conv) {
      newValue = conv.text
      requestAnimationFrame(() => {
        const el = textareaRef.current
        if (el) el.setSelectionRange(conv.cursor, conv.cursor)
      })
    }
    setValue(newValue)

    // Presença "digitando" — só em conversa humana (não no @Atendente), com
    // throttle de ~2.5s. Best-effort (sendTyping engole erros).
    if (activeTab === 'conversas' && newValue.trim()) {
      const now = Date.now()
      if (now - lastTypingRef.current > 2500) {
        lastTypingRef.current = now
        void chatApi.sendTyping(cid)
      }
    }

    const cursor = e.currentTarget.selectionStart ?? newValue.length
    const prefix = detectMentionPrefix(newValue, cursor)

    // Atendente não tem menções (canal privado)
    if (activeTab === 'atendente') {
      setPopoverOpen(false)
      return
    }

    if (prefix !== null) {
      setMentionPrefix(prefix)
      setPopoverOpen(true)
      setHighlightIndex(0)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        fetchMentionable(prefix)
      }, 250)
    } else {
      setPopoverOpen(false)
      setMentionResults([])
    }
  }

  async function fetchMentionable(prefix: string) {
    setMentionLoading(true)
    try {
      const items = await chatApi.mentionable(cid, prefix)
      setMentionResults(items)
      setHighlightIndex(0)
    } catch {
      setMentionResults([])
    } finally {
      setMentionLoading(false)
    }
  }

  function selectMention(user: MentionableUser) {
    const cursor = textareaRef.current?.selectionStart ?? value.length
    const before = value.slice(0, cursor)
    const after = value.slice(cursor)
    const replaced = before.replace(/@[\w.]*$/, `@${user.name} `)
    setValue(replaced + after)
    setPopoverOpen(false)
    setMentionResults([])

    if (user.id === -2) {
      setMentionAll(true)
    } else if (user.robo) {
      // Robô de um colega: a pergunta vai a ELE, pela rota do grupo — antes do
      // ramo `is_bot`, que mandaria ao @Atendente. Sem a rota (tela que não a
      // liga), é uma menção comum ao participante, nunca o @Atendente.
      const robo = user.robo
      if (onAskRobo) setAlvoRobo(robo.bot_user_id)
      else setMentionUserIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]))
    } else if (user.is_bot) {
      // @Atendente: vira pergunta ao bot (/ask), não menção humana
      setAskAtendenteShared(true)
    } else if (user.id > 0) {
      setMentionUserIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]))
    }

    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  // ---------------------------------------------------------------------------
  // Envio
  // ---------------------------------------------------------------------------

  const isSending = sendingAsk || postMessage.isPending

  // Uploads prontos para enviar
  const readyUploads = uploads
    .filter((u) => u.status === 'done' && u.s3_key)
    .map((u) => ({
      s3_key: u.s3_key as string,
      filename: u.file.name,
      mime_type: u.file.type || 'application/octet-stream',
      size_bytes: u.file.size,
    }))

  // Envia se há texto OU uploads prontos (mas não enquanto estiver enviando)
  const canSend = !isSending && (value.trim().length > 0 || readyUploads.length > 0)

  function insertEmoji(emoji: string) {
    const el = textareaRef.current
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? start
    const next = value.slice(0, start) + emoji + value.slice(end)
    setValue(next)
    setEmojiOpen(false)
    requestAnimationFrame(() => {
      const t = textareaRef.current
      if (t) {
        t.focus()
        const p = start + emoji.length
        t.setSelectionRange(p, p)
      }
    })
  }

  async function handleSend() {
    if (!canSend) return

    // Converte emoticons restantes no envio (ex.: um ":)" no fim, sem espaço).
    const trimmed = convertEmoticonsAll(value.trim())

    if (activeTab === 'atendente') {
      // Fluxo privado do Atendente IA
      onAsk(trimmed, alertMessageId, undefined, readyUploads)
      setValue('')
      setMentionUserIds([])
      setMentionAll(false)
      setAskAtendenteShared(false)
      setAlvoRobo(null)
      setUploads([])
      onCancelReply?.()
      return
    }

    const roboAlvo = alvoRobo ?? responderAoRobo?.botUserId ?? null
    if (roboAlvo != null && onAskRobo) {
      // A rota do robô leva só texto: sem ele, ou com anexo, a pergunta não
      // sai — e o anexo fica na caixa em vez de sumir.
      if (!trimmed || readyUploads.length > 0) {
        notifications.show({
          color: 'yellow',
          title: 'Pergunta ao robô',
          message: trimmed
            ? 'O robô não recebe anexo. Tire o arquivo ou mande-o numa mensagem separada.'
            : 'Escreva a pergunta para o robô.',
        })
        return
      }
      onAskRobo(roboAlvo, trimmed)
      setValue('')
      setMentionUserIds([])
      setMentionAll(false)
      setAskAtendenteShared(false)
      setAlvoRobo(null)
      onCancelReply?.()
      return
    }

    // @Atendente selecionado fora da aba privada, OU DM 1:1 com o bot
    // (forceAtendente) → /ask compartilhado (resposta com cartões de ação)
    if (askAtendenteShared || forceAtendente) {
      // Os anexos vão junto (TASK-11729). Antes, esta linha mandava só o texto
      // e o `setUploads([])` logo abaixo apagava a lista: o arquivo subia ao
      // S3 e sumia sem aviso.
      onAsk(trimmed, undefined, true, readyUploads)
      setValue('')
      setMentionUserIds([])
      setMentionAll(false)
      setAskAtendenteShared(false)
      setAlvoRobo(null)
      setUploads([])
      onCancelReply?.()
      return
    }

    // Mensagem humana — canal determinado pela aba
    const channel = activeTab === 'conversas' ? 'conversas' : 'alertas'

    try {
      await postMessage.mutateAsync({
        text: trimmed,
        mentionUserIds,
        mentionAll,
        replyToMessageId: replyingTo?.id ?? null,
        channel,
        ...(readyUploads.length > 0 ? { attachments: readyUploads } : {}),
      })
      setValue('')
      setMentionUserIds([])
      setMentionAll(false)
      setUploads([])
      onCancelReply?.()
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 403) {
        notifications.show({
          color: 'red',
          title: 'Sem permissão',
          message: 'Você não tem acesso para enviar mensagens nesta conversa.',
        })
      } else if (status === 422) {
        notifications.show({
          color: 'orange',
          title: 'Mensagem inválida',
          message: 'A mensagem está vazia ou é muito longa (máx. 4000 caracteres).',
        })
      } else {
        notifications.show({
          color: 'red',
          title: 'Erro ao enviar',
          message: 'Não foi possível enviar a mensagem. Tente novamente.',
        })
      }
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Com o dropdown de menção aberto, o teclado é dele: seta move, Enter/Tab
    // ESCOLHE. Antes o Enter sempre enviava, e quem digitava "@Atendente" e
    // apertava Enter mandava mensagem comum ao time — o bot nunca via a pergunta,
    // e o texto ainda aparecia destacado como se a menção tivesse funcionado.
    if (mentionNavegavel) {
      const n = dropdownItems.length
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightIndex((i) => (Math.min(i, n - 1) + 1) % n)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightIndex((i) => (Math.min(i, n - 1) - 1 + n) % n)
        return
      }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault()
        selectMention(dropdownItems[destacado])
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
    if (e.key === 'Escape') {
      setPopoverOpen(false)
      if (replyingTo) onCancelReply?.()
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (!attachmentsSupported) return
    if (e.clipboardData?.files?.length) {
      e.preventDefault()
      void handleFiles(e.clipboardData.files)
    }
  }

  // Dropdown: @Atendente (compartilhado) + @todos no topo + resultados de busca.
  // O item do bot vem do backend marcado com `is_bot` — o Composer NÃO cria uma
  // entrada sintética própria: fazia isso e o bot aparecia três vezes (a
  // sintética, a que o backend prefixa e o usuário-bot vindo do SELECT).
  // Dentro da aba privada o bot já é o destino, então some da lista.
  const bots = activeTab !== 'atendente' ? mentionResults.filter((u) => u.is_bot) : []
  const humanos = mentionResults.filter((u) => !u.is_bot)
  // @todos também obedece ao prefixo: sem isso ele era o único item de uma busca
  // sem resultado, e o Enter (abaixo) escolheria "todos" para quem digitou outra
  // coisa — mencionar o grupo inteiro por engano.
  const pfxTodos = mentionPrefix.trim().toLowerCase()
  const todos = !pfxTodos || 'todos'.startsWith(pfxTodos) ? [TODOS_ENTRY] : []
  const dropdownItems: MentionableUser[] = [...bots, ...todos, ...humanos]
  // O dropdown só governa o teclado quando está aberto E tem o que escolher.
  const mentionOpen = popoverOpen && activeTab !== 'atendente'
  const mentionNavegavel = mentionOpen && !mentionLoading && dropdownItems.length > 0
  const destacado = Math.min(highlightIndex, dropdownItems.length - 1)

  const replyAuthorName = replyingTo
    ? (replyingTo.author_name ?? replyingTo.kind)
    : null

  return (
    <Paper
      px="md"
      py="sm"
      withBorder={false}
      style={{
        borderTop: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
        background: 'var(--mantine-color-body)',
        flexShrink: 0,
      }}
    >
      {/* Barra de citação */}
      {replyingTo && (
        <Box
          mb={6}
          px={10}
          py={6}
          style={{
            borderLeft: '3px solid var(--mantine-color-teal-5, #0ba5ab)',
            background: 'var(--ai-bg-subtle)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="xs" fw={600} c="teal.7" lineClamp={1}>
              {replyAuthorName}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2} style={{ wordBreak: 'break-word' }}>
              {replyingTo.body ?? ''}
            </Text>
          </Box>
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            onClick={onCancelReply}
            aria-label="Cancelar citação"
            style={{ flexShrink: 0, marginTop: 2 }}
          >
            <IconX size={12} />
          </ActionIcon>
        </Box>
      )}

      {/* Chips de uploads pendentes — só visíveis quando attachmentsSupported */}
      {attachmentsSupported && uploads.length > 0 && (
        <Group gap={6} mb={6} wrap="wrap">
          {uploads.map((u) => (
            <Badge
              key={u.localId}
              variant="light"
              color={u.status === 'error' ? 'red' : u.status === 'done' ? 'teal' : 'gray'}
              rightSection={
                <ActionIcon
                  size={12}
                  variant="transparent"
                  color={u.status === 'error' ? 'red' : 'gray'}
                  onClick={() =>
                    setUploads((prev) => prev.filter((x) => x.localId !== u.localId))
                  }
                  aria-label={`Remover ${u.file.name}`}
                >
                  <IconX size={10} />
                </ActionIcon>
              }
              leftSection={
                u.status === 'uploading' ? (
                  <Loader size={10} />
                ) : u.status === 'done' ? (
                  <IconCheck size={10} />
                ) : null
              }
              style={{ maxWidth: 200 }}
            >
              <Text size="xs" truncate style={{ maxWidth: 140 }}>
                {u.status === 'error' ? `Erro: ${u.file.name}` : u.file.name}
              </Text>
            </Badge>
          ))}
        </Group>
      )}

      <Popover
        opened={popoverOpen && activeTab !== 'atendente'}
        onClose={() => setPopoverOpen(false)}
        position="top-start"
        withArrow={false}
        shadow="md"
        withinPortal
      >
        <Popover.Target>
          <Group gap="xs" align="flex-end" wrap="nowrap" style={{ minWidth: 0 }}>
            {/* Botão de clipe — só visível quando attachmentsSupported */}
            {attachmentsSupported && (
              <ActionIcon
                size="lg"
                variant="subtle"
                color="gray"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Anexar arquivo"
                disabled={isSending}
                style={{ flexShrink: 0 }}
              >
                <IconPaperclip size={18} />
              </ActionIcon>
            )}

            {/* Botão de emoji — abre o seletor (mesmo das reações). Insere no cursor. */}
            <Box style={{ position: 'relative', flexShrink: 0 }}>
              <ActionIcon
                size="lg"
                variant="subtle"
                color="gray"
                onClick={() => setEmojiOpen((v) => !v)}
                aria-label="Inserir emoji"
                disabled={isSending}
              >
                <IconMoodSmile size={18} />
              </ActionIcon>
              {emojiOpen && (
                <Box style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, zIndex: 300 }}>
                  <EmojiPicker inline onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />
                </Box>
              )}
            </Box>

            {/* Input de arquivo oculto */}
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files) void handleFiles(e.target.files)
                e.target.value = ''
              }}
            />

            <Textarea
              ref={textareaRef}
              style={{ flex: 1, minWidth: 0 }}
              placeholder={responderAoRobo ? `Pergunte a ${responderAoRobo.nome}…` : forceAtendente ? (destino ? `Pergunte a ${destino}…` : 'Pergunte ao @Atendente…') : PLACEHOLDERS[activeTab]}
              autosize
              minRows={1}
              maxRows={5}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              aria-activedescendant={mentionNavegavel ? `mention-opt-${destacado}` : undefined}
              onPaste={handlePaste}
              autoFocus
            />
            <ActionIcon
              size="lg"
              color="inovareTeal"
              variant="filled"
              onClick={() => void handleSend()}
              disabled={!canSend}
              aria-label="Enviar mensagem"
              style={{ flexShrink: 0 }}
            >
              {isSending ? <Loader size={14} color="white" /> : <IconSend size={18} />}
            </ActionIcon>
          </Group>
        </Popover.Target>

        <Popover.Dropdown p={4} style={{ minWidth: 220, width: 'min(320px, 90vw)', maxWidth: '90vw' }}>
          <Stack gap={0} role="listbox" aria-label="Sugestões de menção">
            {mentionLoading ? (
              <Group justify="center" p="xs">
                <Loader size="xs" />
              </Group>
            ) : (
              dropdownItems.map((user, idx) => (
                <UnstyledButton
                  key={user.id}
                  id={`mention-opt-${idx}`}
                  role="option"
                  aria-selected={idx === destacado}
                  px="sm"
                  py={6}
                  onClick={() => selectMention(user)}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  style={(theme) => ({
                    borderRadius: theme.radius.sm,
                    // O destaque do teclado usa o MESMO fundo do hover: quem navega
                    // por seta vê o que veria com o mouse em cima.
                    background: idx === destacado ? theme.colors.teal[0] : undefined,
                    ':hover': {
                      background: theme.colors.teal[0],
                    },
                  })}
                >
                  <Box>
                    <Text
                      size="sm"
                      fw={user.id === -2 || user.is_bot ? 600 : 400}
                      c={user.is_bot ? 'violet.6' : user.id === -2 ? 'teal.7' : undefined}
                    >
                      @{user.name}
                    </Text>
                    {/* O e-mail do robô é o sintético interno (`robo+…@consoleai.internal`): não diz nada a quem lê. */}
                    {user.email && !user.robo && (
                      <Text size="xs" c="dimmed">
                        {user.email}
                      </Text>
                    )}
                    {user.is_bot && (
                      <Text size="xs" c="dimmed">
                        {user.robo
                          ? 'Perguntar ao robô — responde só o que todos aqui podem ver'
                          : 'Perguntar ao bot — resposta visível a todos'}
                      </Text>
                    )}
                    {user.id === -2 && (
                      <Text size="xs" c="dimmed">
                        Mencionar todos os participantes
                      </Text>
                    )}
                  </Box>
                </UnstyledButton>
              ))
            )}
            {!mentionLoading && mentionResults.length === 0 && mentionPrefix.length > 0 && (
              <Text size="xs" c="dimmed" p="xs">
                Nenhum usuário encontrado para &ldquo;{mentionPrefix}&rdquo;
              </Text>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Paper>
  )
}
